import { ReactNode } from "react";
import { Box, Dialog, Typography, useTheme } from "@mui/material";

interface PlanDialogShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  children: ReactNode;
}

/**
 * Branded frame for account/billing dialogs: Insight logo header, centred
 * title and one-line subtitle, then content composed by the caller (summary
 * card, fine print, stacked full-width actions). One consistent shell so each
 * confirmation reads as an Insight receipt. Flat UI throughout — borders and
 * background tints only, no shadows or gradients.
 */
export const PlanDialogShell = ({ open, onClose, title, subtitle, children }: PlanDialogShellProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Lighter than MUI's default 50% scrim — the app should stay visible
      // behind these notices, dimmed just enough to focus the dialog.
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0, 0, 0, 0.3)" },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
          backgroundImage: "none",
          width: "100%",
          maxWidth: 440,
        },
      }}
    >
      <Box sx={{ px: { xs: 3, sm: 4 }, pt: 4, pb: 3.5 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
          <Box
            component="img"
            src={isDark ? "/insightLongLogoPrimaryWhiteText.svg" : "/insightLongLogoPrimary.svg"}
            alt="Insight Analytics"
            sx={{ height: 22, width: "auto" }}
          />
        </Box>
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.25rem",
            lineHeight: 1.3,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: theme.palette.text.secondary, mt: 0.5 }}
        >
          {subtitle}
        </Typography>
        <Box sx={{ mt: 3 }}>{children}</Box>
      </Box>
    </Dialog>
  );
};
